<!-- MOLECULE: PageTitle.vue -->

<script setup lang="ts">
//
// Imports
//
import { useI18n } from "vue-i18n";

//
// Constants
//

/**
 * Breadcrumb label-to-path mapping.
 * Maps i18n keys to route paths so that translated labels in any locale
 * resolve to the correct navigation target.
 */
const BREADCRUMB_PATH_MAP: ReadonlyArray<{ i18nKey: string; path: string }> = [
  { i18nKey: "pages.all-visits.title", path: "/app/corporate/all-visits" },
  { i18nKey: "pages.new-results.title", path: "/app/corporate/new-results" },
  { i18nKey: "pages.search.title", path: "/app/corporate/search" },
  { i18nKey: "pages.starred.title", path: "/app/corporate/starred" },
  { i18nKey: "pages.otp-patient.results.title", path: "/app/otp-patient/results" },
  { i18nKey: "pages.otp-patient.balance.title", path: "/app/otp-patient/balance" },
];

//
// Props
//
const props = defineProps<{
  title: string;
}>();

//
// Composables
//
const router = useRouter();
const localePath = useLocalePath();
const { t } = useI18n();

//
// Computed
//

/** Split title into parent and current parts if ` / ` separator is present */
const breadcrumbParts = computed(() => {
  const separator = " / ";
  const idx = props.title.indexOf(separator);
  if (idx === -1) return null;

  return {
    parentLabel: props.title.substring(0, idx),
    currentLabel: props.title.substring(idx + separator.length),
  };
});

/** Resolve parent label to a route path via locale-aware i18n key mapping */
const parentPath = computed<string | null>(() => {
  if (!breadcrumbParts.value) return null;

  const parentLabel = breadcrumbParts.value.parentLabel;

  for (const entry of BREADCRUMB_PATH_MAP) {
    if (t(entry.i18nKey) === parentLabel) {
      return entry.path;
    }
  }

  return null;
});

//
// Methods
//

/** Navigate to the parent page (first breadcrumb segment) */
function navigateToParent(): void {
  if (parentPath.value) {
    void router.push(localePath(parentPath.value));
  }
}
</script>

<template>
  <v-app-bar-title
    class="ml-2 text-primary font-weight-medium"
    style="flex: auto; white-space: nowrap; overflow: visible"
  >
    <!-- Breadcrumb: "Parent / Current" -->
    <template v-if="breadcrumbParts">
      <v-hover v-slot="{ isHovering, props: hoverProps }">
        <span
          v-bind="hoverProps"
          class="cursor-pointer"
          :class="[isHovering ? 'opacity-100' : 'opacity-70']"
          role="link"
          tabindex="0"
          @click="navigateToParent"
          @keydown.enter="navigateToParent"
        >
          {{ breadcrumbParts.parentLabel }}
        </span>
      </v-hover>
      <span class="text-primary mx-2">/</span>
      <span>{{ breadcrumbParts.currentLabel }}</span>
    </template>

    <!-- Plain title (no separator) -->
    <template v-else>
      {{ title }}
    </template>
  </v-app-bar-title>
</template>
